import { PHONE_COUNTRY_DEFS } from '@/utils/phoneCountryData';
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  detectBrowserCountryCode as detectBrowserPhoneCountry,
} from '@/utils/phoneCountryDetection';

export type PhoneCountryConfig = {
  /** ISO 3166-1 alpha-2 */
  code: string;
  name: string;
  dialCode: string;
  /** Max national digits (without country code) */
  nationalLength: number;
  minLength: number;
  flag: string;
  /** Lowercase search index (name, code, dial code, aliases). */
  searchText: string;
};

function isoToFlagEmoji(code: string): string {
  const normalized = code.toUpperCase();
  if (normalized.length !== 2 || normalized === 'ZZ') return '🌐';
  return normalized.replace(/./g, (char) =>
    String.fromCodePoint(127397 + char.charCodeAt(0)),
  );
}

function normalizeSearchToken(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function buildSearchText(def: (typeof PHONE_COUNTRY_DEFS)[number]): string {
  const tokens = [
    def.code,
    def.name,
    def.dialCode,
    `+${def.dialCode}`,
    ...(def.aliases ?? []),
  ];
  return normalizeSearchToken(tokens.join(' '));
}

/** Dial-code metadata for validation and the phone input UI. Sorted alphabetically by name. */
export const PHONE_COUNTRIES: PhoneCountryConfig[] = PHONE_COUNTRY_DEFS.map((def) => ({
  code: def.code,
  name: def.name,
  dialCode: def.dialCode,
  nationalLength: def.nationalLength,
  minLength: def.minLength,
  flag: isoToFlagEmoji(def.code),
  searchText: buildSearchText(def),
})).sort((a, b) => a.name.localeCompare(b.name));

const DEFAULT_COUNTRY_CODE = DEFAULT_PHONE_COUNTRY_CODE;

const GENERIC_PHONE_COUNTRY: PhoneCountryConfig = {
  code: 'ZZ',
  name: 'International',
  dialCode: '',
  nationalLength: 15,
  minLength: 7,
  flag: '🌐',
  searchText: 'international zz',
};

const byCode = new Map(PHONE_COUNTRIES.map((c) => [c.code, c]));
const byDialCode = [...PHONE_COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

export function getPhoneCountry(code: string | undefined | null): PhoneCountryConfig {
  if (!code) return byCode.get(DEFAULT_COUNTRY_CODE) ?? PHONE_COUNTRIES[0];
  return byCode.get(code.toUpperCase()) ?? { ...GENERIC_PHONE_COUNTRY, code: code.toUpperCase() };
}

/** Infer country: browser timezone → browser locale → India. */
export function detectBrowserCountryCode(): string {
  return detectBrowserPhoneCountry();
}

export function findCountryByDialDigits(digits: string): PhoneCountryConfig | null {
  for (const country of byDialCode) {
    if (digits.startsWith(country.dialCode)) return country;
  }
  return null;
}

export function filterPhoneCountries(query: string): PhoneCountryConfig[] {
  const trimmed = query.trim();
  if (!trimmed) return PHONE_COUNTRIES;

  const normalized = normalizeSearchToken(trimmed);
  const dialDigits = trimmed.replace(/\D/g, '');

  return PHONE_COUNTRIES.filter((country) => {
    if (country.searchText.includes(normalized)) return true;
    if (!dialDigits) return false;
    return (
      country.dialCode.includes(dialDigits) ||
      dialDigits.includes(country.dialCode) ||
      country.dialCode.startsWith(dialDigits)
    );
  });
}

export { DEFAULT_COUNTRY_CODE };
